(ns client-portal.client.voice-insights.charts.views
  (:require [client-portal.client.voice-insights.charts.data :as charts.d]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [client-portal.client.voice-insights.sessions.data :as sessions.d]
            [clojure.string :as s]
            [shared.core.regexes :as regexes]
            [shared.core.components.components :as c]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.components.recharts-components :as recharts.cmp]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.router :as router]
            [shared.core.util :as util]
            [shared.core.style :as style]))

(defn chart-from-config [_]
  (let [sessions-query-params- (re/subscribe [:sessions/relevant-query-params-])]
    (fn [{:keys [chart-type data config-overrides include-session-double-click? tag-name filter-tags filter-tags-fn
                 show-time-period-toggle? selected-time-period session-double-click-config selected-value-mode
                 show-value-mode-toggle? include-day-part-time-period? show-legend default-filter-values
                 labels]
          :as chart-config}]
      (if chart-type
        (let [chart-config (cond-> chart-config
                                   (not (nil? show-legend))
                                   (assoc :legend? show-legend
                                          :show-legend? show-legend))
              sessions-query-params @sessions-query-params-
              chart-type->cmp {"AreaChart" recharts.cmp/area-chart
                               "BarChart" recharts.cmp/bar-chart
                               "LineChart" recharts.cmp/line-chart
                               "PieChart" recharts.cmp/donut-chart
                               "RadarChart" recharts.cmp/radar-chart
                               "ScatterChart" recharts.cmp/scatter-chart
                               "Sankey" recharts.cmp/sankey-chart}
              chart-cmp (get chart-type->cmp chart-type)
              sankey? (= chart-type "Sankey")
              area? (= chart-type "AreaChart")
              bar? (= chart-type "BarChart")
              line? (= chart-type "LineChart")
              pie? (= chart-type "PieChart")
              radar? (= chart-type "RadarChart")
              scatter? (= chart-type "ScatterChart")
              height (cond
                       sankey? nil ;; Calculated by component
                       :else 500)
              row-get (fn [row k]
                        (when k
                          (or (get row (util/->kebab-case-keyword k))
                              (get row (keyword k))
                              (get row k))))
              name-key (or (:x-key chart-config) (:name-key chart-config) :name)
              ;; Display only — the data keeps the raw value so the drill-in still gets
              ;; the id. Values absent from `labels` display unchanged.
              label-of (fn [v] (get labels (str v) v))
              tick-label-of (fn [payload] (label-of (:value payload)))
              on-double-click-handler (fn [e d]
                                        (let [payload (or (:payload d) d)
                                              payload-get #(row-get payload %)
                                              payload-name (payload-get name-key)
                                              raw-time-value (or (:raw_time_value payload)
                                                                 (:raw-time-value payload))
                                              shift-parts (when (and show-time-period-toggle?
                                                                     (= selected-time-period "shift"))
                                                            (let [raw (str (or raw-time-value payload-name))
                                                                  idx (s/index-of raw " ")]
                                                              {:daily (if idx (subs raw 0 idx) raw)
                                                               :day-part (if idx (subs raw (inc idx)) "")}))
                                              time-filter (when show-time-period-toggle?
                                                            (condp = selected-time-period
                                                              "hourly"
                                                              (let [hour (-> payload-name
                                                                             (s/lower-case)
                                                                             (s/replace " " ""))]
                                                                {:by "time-of-day"
                                                                 :condition "include"
                                                                 :join "or"
                                                                 :value [{:name hour
                                                                          :value (util/time-of-day-with-leading-zero
                                                                                   hour)}]})

                                                              "day-of-week"
                                                              {:by "day-of-week"
                                                               :condition "include"
                                                               :join "or"
                                                               :value [{:name payload-name
                                                                        :value payload-name}]}

                                                              "day-part"
                                                              {:by "day-part"
                                                               :condition "include"
                                                               :join "or"
                                                               :value [{:name (util/->capitalize-words
                                                                                (or raw-time-value payload-name))
                                                                        :value (or raw-time-value payload-name)}]}

                                                              "shift"
                                                              (let [day-part (:day-part shift-parts)]
                                                                {:by "day-part"
                                                                 :condition "include"
                                                                 :join "or"
                                                                 :value [{:name (util/->capitalize-words day-part)
                                                                          :value day-part}]})

                                                              nil))
                                              filter-ctx {:selected-time-period selected-time-period
                                                          :time-filter time-filter
                                                          :payload payload
                                                          :payload-name payload-name
                                                          :raw-time-value raw-time-value}
                                              filters (if filter-tags-fn
                                                        (let [r (filter-tags-fn d filter-ctx)]
                                                          (if (sequential? r) r [r]))
                                                        (let [tag-value (or (when (:stack-id chart-config)
                                                                              (some-> (:tooltip-state* d)
                                                                                      (deref)
                                                                                      :hovered-key))
                                                                            payload-name)]
                                                          [time-filter (cond
                                                                         filter-tags
                                                                         filter-tags

                                                                         (and tag-name tag-value)
                                                                         {:by "tags"
                                                                          :condition "include"
                                                                          :join "or"
                                                                          :value [{:name tag-name
                                                                                   :value tag-value}]})]))
                                              comparison-filters (->> (:comparison-filters session-double-click-config)
                                                                      (keep (fn [{:keys [by op column]}]
                                                                              (when by
                                                                                (let [v (payload-get column)]
                                                                                  (when (and (some? v)
                                                                                             (or (not= by "utterance-start")
                                                                                                 (pos? (util/parse-int v))))
                                                                                    {:by by
                                                                                     :condition "include"
                                                                                     :join "and"
                                                                                     :value [{:op op :value (str v)}]}))))))
                                              fd (->> (concat filters comparison-filters)
                                                      (filterv identity)
                                                      (sessions.d/filters->query-param))
                                              ;; Seed the drill-in with the report's default filter values,
                                              ;; but let the current route's query params win (merge under).
                                              base-query-params (merge (sessions.d/default-filter-values->session-qps default-filter-values)
                                                                       sessions-query-params)
                                              {:keys [open-utterances?]} session-double-click-config]
                                          (router/open-route-based-on-event
                                            e
                                            [:vi/sessions
                                             nil
                                             (cond-> (assoc base-query-params
                                                       :fd fd)

                                                     open-utterances?
                                                     (assoc :svm "utterances")

                                                     (and show-time-period-toggle?
                                                          (= selected-time-period "daily")
                                                          raw-time-value)
                                                     (assoc :tf -1
                                                            :start raw-time-value
                                                            :end raw-time-value)

                                                     (and show-time-period-toggle?
                                                          (= selected-time-period "shift")
                                                          (:daily shift-parts))
                                                     (assoc :tf -1
                                                            :start (:daily shift-parts)
                                                            :end (:daily shift-parts)))]
                                            :on-route-change #(re/dispatch [:sessions/reload-conversations-list]))))
              transf-fn-name->transf-fn #(or (get {"capitalize-words" util/->capitalize-words}
                                                  %)
                                             identity)]
          [chart-cmp
           (cond->> (cond-> (-> (dissoc chart-config :chart-type)
                                (assoc :height height
                                       :tooltip? (or (not pie?) include-session-double-click?)
                                       :tooltip-config (cond-> {:show-double-click-message? include-session-double-click?
                                                                :header-transf-name-fn (if labels
                                                                                         label-of
                                                                                         (-> chart-config
                                                                                             :tooltip-config
                                                                                             :header-transf-fn-name
                                                                                             (transf-fn-name->transf-fn)))}
                                                               ;; The line/area tooltip labels rows through this rather
                                                               ;; than header-transf-name-fn.
                                                               labels
                                                               (assoc :label-transf-fn label-of)
                                                               ;; Only set a label transform chart-card can resolve by name;
                                                               ;; leaving it unset lets a `config-overrides` transform win.
                                                               (-> chart-config :tooltip-config :label-transf-fn-name)
                                                               (assoc :label-transf-fn (-> chart-config
                                                                                           :tooltip-config
                                                                                           :label-transf-fn-name
                                                                                           (transf-fn-name->transf-fn)))
                                                               show-value-mode-toggle?
                                                               (assoc :label-transf-fn (fn [_] "Count")))))
                            (or labels (-> chart-config :legend-config :display-fn-name))
                            (assoc :legend-config {:display-fn (if labels
                                                                 label-of
                                                                 (-> chart-config
                                                                     :legend-config
                                                                     :display-fn-name
                                                                     (transf-fn-name->transf-fn)))})

                            ;; A horizontal bar chart holds categories on Y, values on X.
                            (and labels (:horizontal? chart-config))
                            (update :y-axis-config merge {:value-transf-fn tick-label-of})

                            (and labels (not (:horizontal? chart-config)))
                            (update :x-axis-config merge {:value-transf-fn tick-label-of})

                            labels
                            (assoc :label-fn label-of))

                    (= selected-value-mode "sessions")
                    (util/deep-merge {:y-axis-config {:value-transf-fn (fn [p] (str (js/Math.round (:value p)) "%"))}})

                    pie?
                    (util/deep-merge {:label-fn identity
                                      :cell-config (cond-> {}
                                                           include-session-double-click?
                                                           (assoc :on-double-click (fn [e payload]
                                                                                     (on-double-click-handler e payload))))})

                    sankey?
                    (util/deep-merge (merge {:margin {:left 20 :right 160 :top 20 :bottom 20}
                                             :dynamic-margins? true}
                                            (some-> (:data chart-config)
                                                    (util/->kebab-case-keyword-top-level))))

                    bar?
                    (util/deep-merge (cond-> {:legend? true
                                              :stack-id (:stack-id chart-config)
                                              :reference-areas (:reference-areas chart-config)
                                              :alternate-x-set (:alternate-x-set chart-config)
                                              :x-key (or (:x-key chart-config) (:name-key chart-config))
                                              :bar-config (cond-> {}
                                                                  include-session-double-click?
                                                                  (assoc :on-double-click (fn [click-data _ e]
                                                                                            (on-double-click-handler e click-data))))
                                              :margin {:top 20 :right 30 :left 20 :bottom (if include-day-part-time-period?
                                                                                            25
                                                                                            0)}}
                                             (not (:horizontal? chart-config))
                                             (assoc :chart-height height
                                                    :x-axis-config {:angle -45})))

                    area?
                    (util/deep-merge {:show-legend? true
                                      :x-key (:name-key chart-config)
                                      :x-axis-config {:angle -45}
                                      :y-keys (:data-keys chart-config)
                                      :y-axis-visible? true
                                      :height height
                                      :width "100%"})

                    radar?
                    (util/deep-merge {:show-legend? true
                                      :axis-key (or (:data-key chart-config) (:name-key chart-config))
                                      :height height
                                      :width "100%"})

                    scatter?
                    (util/deep-merge {:show-legend? true
                                      :x-key (some-> chart-config :data-keys (first))
                                      :y-key (when (some-> chart-config :data-keys (count) (>= 2))
                                               (some-> chart-config :data-keys (second)))
                                      :z-key (when (some-> chart-config :data-keys (count) (= 3))
                                               (some-> chart-config :data-keys (last)))
                                      :x-axis-config {:angle -45}
                                      :height height
                                      :width "100%"})

                    line?
                    (util/deep-merge {:show-legend? true
                                      :scrollable? true
                                      :fill-width? true
                                      :chart-height height
                                      :dot? true
                                      :smooth? (boolean (:smooth? chart-config))
                                      :x-key (or (:x-key chart-config) (:name-key chart-config))
                                      :x-axis-config {:angle -45}
                                      :y-keys (:data-keys chart-config)
                                      :y-axis-visible? true
                                      :animation-duration (if show-time-period-toggle?
                                                            500
                                                            1000)
                                      :min-width 600
                                      :margin {:top 20 :right 10 :left 0 :bottom (if include-day-part-time-period?
                                                                                   25
                                                                                   0)}})

                    config-overrides
                    (util/deep-merge config-overrides))])
        [:div
         [:code
          (str chart-config)]]))))

(defn sort-by-pills
  "Renders a `Sort By` pill group. `sort-options` is a vector of `{:label :value :sort-fn}` maps; the
   `:sort-fn` is used by `chart-card` to sort the chart data descending."
  [{:keys [settings-path selected-sort sort-options]}]
  [:div {:style {:display :flex
                 :align-items :center
                 :gap 8}}
   [:span {:style {:color (style/alc-color "Text/primary")
                   :font-size 13
                   :font-weight 500
                   :line-height 1.2}}
    "Sort By"]
   [alc/pill-button-group {:selected-value selected-sort
                           :form-path settings-path
                           :form-name :sort
                           :options (mapv #(select-keys % [:label :value]) sort-options)}]])

(defn threshold-filter-toolbar
  "Renders the bottom filter toolbar (`<label> [More/Less Than] [count] <unit>s`). `chart-card`
   auto-manages the associated form state and applies the filter to the chart data."
  [{:keys [settings-path enabled? operator-value count-value default-value label unit]}]
  (let [text-style (cond-> {:color (style/alc-color "Text/primary")
                            :font-size 16
                            :font-weight 400
                            :line-height 1.43
                            :flex "0 0 auto"
                            :user-select :none}
                           (not enabled?)
                           (assoc :opacity 0.5))]
    [:div {:style {:display :flex
                   :align-items :center
                   :gap 8}}
     [alc/up-checkbox {:form-path settings-path
                       :form-name :threshold-enabled
                       :checked enabled?}]
     [:span {:style text-style}
      (or label "Only show items with at least")]
     [alc/up-dropdown {:form-path settings-path
                       :form-name :threshold-operator
                       :value operator-value
                       :disabled (not enabled?)
                       :placeholder "More Than"
                       :options [{:text "More Than" :value "more-than"}
                                 {:text "Less Than" :value "less-than"}]
                       :style {:max-width 160}}]
     [alc/up-input (cond-> {:form-path settings-path
                            :form-name :threshold-count
                            :regex regexes/number??
                            :placeholder "Count"
                            :disabled (not enabled?)
                            :style {:max-width 65}}
                           (some? default-value)
                           (assoc :default-value (str default-value)))]
     (when unit
       [:span {:style text-style}
        (util/pluralize unit (or count-value 0))])]))

(defn- top-n-rows
  "The rows a chart shows while its Top N toggle is on, ranked by each row's total:
   the sum of `data-keys` (a stacked/grouped chart), else `data-key`, else a `value`
   column. Rows past the cut fold into one `Other` row, summed PER ranked key so a
   multi-series chart still stacks.

   When none of those keys carry a value on the rows — a chart whose series arrive
   in a `key-for-data-keys` blob, or one that projects something else entirely —
   there is nothing to rank by, so the query's own ordering stands and the first N
   rows are taken as they came."
  [rows {:keys [name-key data-key data-keys top-n-toggle]}]
  (let [candidates (cond
                     (not-empty data-keys) (vec data-keys)
                     data-key [data-key]
                     :else ["value"])
        val-ks (not-empty (filterv (fn [k] (some #(some? (get % k)) rows)) candidates))
        sum-key (fn [rows k] (->> rows (keep #(get % k)) (filterv number?) (apply +)))
        row-total (fn [row] (reduce + (keep #(when (number? (get row %)) (get row %)) val-ks)))]
    (if-not val-ks
      (vec (take top-n-toggle rows))
      (let [sorted (vec (sort-by row-total #(compare %2 %1) rows))
            other (reduce (fn [row k]
                            (let [total (sum-key (drop top-n-toggle sorted) k)]
                              (cond-> row
                                      (pos? total) (assoc k total))))
                          {name-key "Other"}
                          val-ks)]
        (cond-> (vec (take top-n-toggle sorted))
                (> (count other) 1) (conj other))))))

(defn chart-card
  "`self-load?` (default true) is the dashboard behavior: with no `:data` prop the card
   fetches itself from `/api/vi/charts/by-id` using `chart-id`. Callers whose data is
   already resolved elsewhere — report grids, where `chart-id` is only a local key for
   form state — must pass `:self-load? false` so a missing/nil `:data` renders empty
   instead of requesting a chart id the endpoint has never heard of."
  [{:keys [chart-id data self-load?] :or {self-load? true}}]
  (let [data- (when (and self-load? (not data))
                (re/subscribe [:voice-insights/chart-data- chart-id]))
        settings-path (vec (conj charts.d/settings-path chart-id))
        selected-top-n- (re/subscribe [:form/val- settings-path :top-n])
        selected-time-period- (re/subscribe [:form/val- settings-path :time-period])
        selected-value-mode- (re/subscribe [:form/val- settings-path :value-mode])
        selected-sort- (re/subscribe [:form/val- settings-path :sort])
        selected-view-mode- (re/subscribe [:form/val- settings-path :view-mode])
        threshold-enabled- (re/subscribe [:form/val- settings-path :threshold-enabled])
        threshold-operator- (re/subscribe [:form/val- settings-path :threshold-operator])
        threshold-count- (re/subscribe [:form/val- settings-path :threshold-count])
        threshold-initialized?* (re/ratom false)
        tag-names-set- (re/subscribe [:vi-tags/names-set-by-current-concept-id-])
        day-parts- (re/subscribe [:day-parts/by-current-concept-id-])]
    (re/create-class
      {:component-did-mount
       (fn [this]
         (let [{:keys [add-listener-dispatch?]} (re/props this)]
           (when add-listener-dispatch?
             (re/dispatch [:voice-insights/chart-initialize-listener chart-id false]))))

       :reagent-render
       (fn [{:keys [title description name-key data-key key-for-data-keys top-n-toggle show-time-period-toggle?
                    required-tag-names data-keys data include-day-part-time-period? sort-options threshold-filter
                    time-period-key show-value-mode-toggle? value-modes show-view-mode-toggle?
                    use-animated-loading-block? chart-height header-icon-type]
             :or {time-period-key "time_grouping"
                  show-view-mode-toggle? true}
             :as chart-config}]
         (let [tag-names-set @tag-names-set-
               chart-data (or data (when data- @data-))]
           (when (or (not required-tag-names)
                     (every? #(contains? tag-names-set %) required-tag-names))
             (let [include-day-part-time-period? (and include-day-part-time-period?
                                                      (not (util/loading? @day-parts-))
                                                      (not-empty @day-parts-))
                   selected-view-mode (when show-view-mode-toggle?
                                        (or @selected-view-mode- "chart"))
                   table-mode? (= selected-view-mode "table")
                   table-data (when table-mode?
                                (let [d (util/when-not-loading chart-data)]
                                  (cond
                                    (and (map? d) (:links d) (:nodes d))
                                    (let [nodes (:nodes d)]
                                      (mapv (fn [{:keys [source target value]}]
                                              {"source" (get-in nodes [source :name])
                                               "target" (get-in nodes [target :name])
                                               "value" value})
                                            (:links d)))

                                    (vector? d)
                                    (if (and (not-empty d) key-for-data-keys)
                                      (mapv (fn [r]
                                              (merge (dissoc r key-for-data-keys)
                                                     (some-> (get r key-for-data-keys)
                                                             (util/json-string->map-basic))))
                                            d)
                                      d))))
                   table-data? (not-empty table-data)
                   selected-top-n (when (and (not (util/loading? chart-data))
                                             (not-empty chart-data)
                                             top-n-toggle
                                             (>= (count chart-data) top-n-toggle))
                                    (or @selected-top-n- "top-n"))
                   selected-time-period (when (and (not (util/loading? chart-data))
                                                   (not-empty chart-data)
                                                   show-time-period-toggle?)
                                          (or @selected-time-period- (if include-day-part-time-period?
                                                                       "shift"
                                                                       "daily")))
                   value-modes (when show-value-mode-toggle?
                                 (or value-modes
                                     [{:label "By Total"
                                       :value "count"
                                       :data-keys ["value"]
                                       :data-key "value"
                                       :config-overrides {:tooltip-config {:label-transf-fn (fn [_] "Total")}}}
                                      {:label "By Sessions"
                                       :value "sessions"
                                       :data-keys ["session_value"]
                                       :data-key "session_value"
                                       :config-overrides {:tooltip-config {:label-transf-fn (fn [_] "Percent of Sessions")
                                                                           :value-transf-fn (fn [v _] (str v "%"))}}}]))
                   selected-value-mode (when (and (not (util/loading? chart-data))
                                                  (not-empty chart-data)
                                                  (seq value-modes))
                                         (or @selected-value-mode- (:value (first value-modes))))
                   active-value-mode (when selected-value-mode
                                       (first (filter #(= (:value %) selected-value-mode) value-modes)))
                   data-keys (or (:data-keys active-value-mode) data-keys)
                   data-key (or (:data-key active-value-mode) data-key)
                   key-for-data-keys (or (:key-for-data-keys active-value-mode) key-for-data-keys)
                   chart-config (assoc chart-config :data-keys (or data-keys
                                                                   (when (and (not (util/loading? chart-data))
                                                                              (not-empty chart-data)
                                                                              key-for-data-keys)
                                                                     (if show-time-period-toggle?
                                                                       (->> chart-data
                                                                            (map second)
                                                                            (flatten)
                                                                            (map #(-> (get % key-for-data-keys)
                                                                                      (util/json-string->map-basic)
                                                                                      (keys)))
                                                                            (flatten)
                                                                            (distinct)
                                                                            (vec))
                                                                       (->> chart-data
                                                                            (map #(-> (get % key-for-data-keys)
                                                                                      (util/json-string->map-basic)
                                                                                      (keys)))
                                                                            (flatten)
                                                                            (distinct)
                                                                            (vec))))))
                   chart-config (cond-> chart-config
                                        active-value-mode
                                        (-> (assoc :data-key data-key
                                                   :key-for-data-keys key-for-data-keys)
                                            (update :config-overrides util/deep-merge
                                                    (:config-overrides active-value-mode))))
                   chart-data (if (and show-time-period-toggle?
                                       (vector? chart-data)
                                       (some #(get % time-period-key) chart-data))
                                (group-by #(get % time-period-key) chart-data)
                                chart-data)
                   day-part-start-times (when (contains? #{"day-part" "shift"} selected-time-period)
                                          (->> (util/when-not-loading @day-parts-)
                                               (map (fn [dp] [(:label dp) (:start-time dp)]))
                                               (into {})))
                   data (cond
                          (= selected-time-period "day-part")
                          (->> (get chart-data selected-time-period)
                               (sort-by #(get day-part-start-times (get % "raw_time_value") "99:99:99"))
                               (vec))

                          (= selected-time-period "shift")
                          (->> (get chart-data selected-time-period)
                               (sort-by (fn [row]
                                          (let [raw (get row "raw_time_value" "")
                                                idx (s/index-of raw " ")
                                                daily (if idx (subs raw 0 idx) raw)
                                                day-part (if idx (subs raw (inc idx)) "")]
                                            [daily (get day-part-start-times day-part "99:99:99")])))
                               (vec))

                          selected-time-period
                          (get chart-data selected-time-period)

                          (= selected-top-n "top-n")
                          (let [rows (util/when-not-loading chart-data)]
                            (if (sequential? rows)
                              (top-n-rows (vec rows) chart-config)
                              rows))

                          :else
                          chart-data)
                   data? (not-empty (util/when-not-loading data))
                   data (if (and data? key-for-data-keys)
                          (mapv (fn [r]
                                  (merge (dissoc r key-for-data-keys)
                                         (some-> (get r key-for-data-keys) (util/json-string->map-basic))))
                                data)
                          data)
                   selected-sort (when (and data? (seq sort-options))
                                   (or @selected-sort- (:value (first sort-options))))
                   threshold-value-fn (:value-fn threshold-filter)
                   threshold-enabled? (if (nil? @threshold-enabled-) true @threshold-enabled-)
                   threshold-operator (or @threshold-operator- "more-than")
                   threshold-count (some-> @threshold-count- (util/parse-int))
                   threshold-op (if (= threshold-operator "less-than") < >)
                   threshold-default-limit (when (and threshold-filter
                                                      threshold-value-fn
                                                      data?
                                                      (:auto-limit-top-n threshold-filter))
                                             (let [top-n (:auto-limit-top-n threshold-filter)]
                                               (when (> (count data) top-n)
                                                 (some-> (->> data
                                                              (sort-by threshold-value-fn)
                                                              (reverse)
                                                              (take top-n)
                                                              (last))
                                                         (threshold-value-fn)
                                                         (dec)
                                                         ((fn [v] (when (pos? v) v)))))))
                   _ (when (and threshold-filter data? (not @threshold-initialized?*))
                       (when threshold-default-limit
                         (re/dispatch [:form/change settings-path :threshold-count threshold-default-limit]))
                       (reset! threshold-initialized?* true))
                   prepped-data (if data?
                                  (cond->> data
                                           (and threshold-filter threshold-value-fn threshold-enabled?
                                                threshold-count (pos? threshold-count))
                                           (filterv (fn [item] (threshold-op (threshold-value-fn item)
                                                                             threshold-count)))

                                           (and selected-sort (seq sort-options))
                                           ((fn [d]
                                              (if-let [sort-fn (->> sort-options
                                                                    (filter #(= (:value %) selected-sort))
                                                                    (first)
                                                                    :sort-fn)]
                                                (vec (reverse (sort-by sort-fn d)))
                                                d))))
                                  data)
                   prepped-data? (not-empty (util/when-not-loading prepped-data))
                   ;; On the shift chart, group rows by day so we can (a) tint alternate days
                   ;; and (b) inject a spacer column between days for a little separation.
                   shift-day-groups (when (and prepped-data? (= selected-time-period "shift"))
                                      (let [day-of (fn [r]
                                                     (let [raw (str (or (get r "raw_time_value")
                                                                        (get r name-key)))
                                                           idx (s/index-of raw " ")]
                                                       (if idx (subs raw 0 idx) raw)))]
                                        (partition-by day-of prepped-data)))
                   ;; Blank (zero-width) spacer category between days. Each gets a unique number
                   ;; of zero-width spaces so the categories stay distinct but render no label.
                   chart-render-data (if shift-day-groups
                                       (->> shift-day-groups
                                            (map-indexed (fn [i grp]
                                                           (if (zero? i)
                                                             grp
                                                             (cons {name-key (apply str (repeat i "​"))}
                                                                   grp))))
                                            (apply concat)
                                            (vec))
                                       prepped-data)]
               [c/interactive
                alc/card {:title title
                          :subtitle description
                          ;; `alc/card` draws it as the bordered block beside the
                          ;; title, as the VI dashboard cards do.
                          :material-icon-type header-icon-type
                          :title-inline-contents (when selected-view-mode
                                                   [[:div {:style {:margin-left :auto
                                                                   :flex-shrink 0}}
                                                     [alc/up-toggle-group
                                                      {:type "single"
                                                       :value selected-view-mode
                                                       :on-value-change (fn [v]
                                                                          (when (seq v)
                                                                            (re/dispatch [:form/change settings-path :view-mode v])))
                                                       :items [{:value "chart"
                                                                :content [alc/material-icon {:icon-type :bar-chart
                                                                                             :style {:font-size 16}}]}
                                                               {:value "table"
                                                                :content [alc/material-icon {:icon-type :table-chart
                                                                                             :style {:font-size 16}}]}]}]]])}
                (when (or (not-empty title) (not-empty description) selected-top-n selected-time-period)
                  [:div {:style {:display :flex
                                 :flex-direction :row
                                 :align-items :flex-start
                                 :justify-content :space-between}}
                   [:div {:style {:display :flex
                                  :flex-direction :column
                                  :gap 12}}
                    (when (and (not table-mode?) selected-top-n)
                      [alc/pill-button-group {:selected-value selected-top-n
                                              :form-path settings-path
                                              :form-name :top-n
                                              :options [{:label (str "Top " top-n-toggle) :value "top-n"}
                                                        {:label "All" :value "all"}]}])
                    (when (and (not table-mode?) selected-time-period)
                      [alc/pill-button-group {:selected-value selected-time-period
                                              :form-path settings-path
                                              :form-name :time-period
                                              :options (filterv identity
                                                                [(when (and include-day-part-time-period?
                                                                            (not-empty (util/when-not-loading @day-parts-)))
                                                                   {:label "Shift" :value "shift"})
                                                                 {:label "Daily" :value "daily"}
                                                                 {:label "Day of Week" :value "day-of-week"}
                                                                 {:label "Hourly" :value "hourly"}
                                                                 (when (and include-day-part-time-period?
                                                                            (not-empty (util/when-not-loading @day-parts-)))
                                                                   {:label "Day Part" :value "day-part"})
                                                                 {:label "Total" :value "all"}])}])
                    (when (and (not table-mode?) data? (seq sort-options))
                      [sort-by-pills {:settings-path settings-path
                                      :selected-sort selected-sort
                                      :sort-options sort-options}])]
                   (when (and (not table-mode?) show-value-mode-toggle?)
                     [alc/pill-button-group {:selected-value selected-value-mode
                                             :form-path settings-path
                                             :form-name :value-mode
                                             :options (mapv #(select-keys % [:label :value]) value-modes)}])])
                (cond
                  (util/loading? prepped-data)
                  (if use-animated-loading-block?
                    ;; Sized to the chart it stands in for, so nothing shifts when the
                    ;; data lands.
                    [c/loader-rectangle {:height (or chart-height 500)
                                         :style {:border-radius 8}}]
                    [alc/up-loader-with-label {:label "Loading Chart..."
                                               :style {:padding 60}}])

                  table-mode?
                  (if table-data?
                    (let [header-keys (->> table-data (mapcat keys) (distinct) (vec))
                          fmt (fn [v]
                                (cond
                                  (nil? v) "—"
                                  (number? v) (util/readable-number v)
                                  :else (str v)))]
                      [:div {:style {:display :flex
                                     :flex-direction :column
                                     :gap 12}}
                       [alc/table {:headers (mapv (fn [k] {:label (util/->capitalize-words k)}) header-keys)
                                   :rows (mapv (fn [row]
                                                 {:cells (mapv (fn [k] (fmt (get row k))) header-keys)})
                                               table-data)}]
                       [:div {:style {:display :flex
                                      :justify-content :flex-end}}
                        [alc/up-button {:icon-type :DownloadIcon
                                        :on-click #(re/dispatch [:core/download
                                                                 {:filename (str (util/->kebab-case-string (or (not-empty title) "chart-data")) ".csv")
                                                                  :headers (mapv util/->capitalize-words header-keys)
                                                                  :rows (mapv (fn [row] (mapv (fn [k] (get row k)) header-keys)) table-data)}])}
                         "Download CSV"]]])
                    [empty-state/no-chart-data {}])

                  data?
                  [:div {:style {:display :flex
                                 :flex-direction :column
                                 :gap 16}}
                   (if prepped-data?
                     [chart-from-config (assoc chart-config
                                          :data chart-render-data
                                          :selected-time-period selected-time-period
                                          :selected-value-mode selected-value-mode)]
                     [empty-state/no-chart-data {}])
                   (when threshold-filter
                     [threshold-filter-toolbar {:settings-path settings-path
                                                :enabled? threshold-enabled?
                                                :operator-value threshold-operator
                                                :count-value threshold-count
                                                :default-value threshold-default-limit
                                                :label (:label threshold-filter)
                                                :unit (:unit threshold-filter)}])]

                  :else
                  [empty-state/no-chart-data {}])]))))})))

(defn chart-dashboard [{:keys [configs]}]
  (->> configs
       (mapv (fn [conf]
               (if (map? conf)
                 [chart-card conf]
                 (->> conf
                      (mapv (fn [inner-conf] [chart-card inner-conf]))
                      (into [:div {:style {:display :flex
                                           :gap 12}}])))))
       (into [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 16}}])))
